<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Address',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    parent: {
      type: Object,
      required: true,
    },
    item: {
      type: Object,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const resource = ref(null);
  const editor = ref(null);
  const createTitle = ref($fn.value.pgettext('Addresses', 'Add address'));
  const editTitle = ref($fn.value.pgettext('Addresses', 'Edit address'));
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    resource: resource.value,
    editor: editor.value,
    createTitle: createTitle.value,
    editTitle: editTitle.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_resource = () => {
    return props.item || $fn.value.schemaDefaults('CreateCompanyAddress');
  };
  const $$load_editor = () => {
    return resource.value.editor();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'resource' },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'createTitle' },
      { name: 'editTitle' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { resource: $$load_resource, editor: $$load_editor },
    ux: {},
    dataRefs: {
      resource,
      editor,
      createTitle,
      editTitle,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: resource.value.id == null ? createTitle.value : editTitle.value,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.parent,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parent');
    }
  );
  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_bo84yuriw_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeComponent_lni5jznvp_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_closeComponent_m2kbwm2rm_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: 'deleted',
      },
    };
  };
  const act_1040167445267876_rest_qfg5vz38s_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: props.parent.addresses,
      resource: editor.value,
    };
  };

  const act_7315092382398562_closeModal_bo84yuriw =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_bo84yuriw_getActionArgs,
    });
  const inst_evh_7315092382398562_close_haev4c9f9 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_bo84yuriw,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_haev4c9f9],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_tze6f8epw =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_ossic7t88 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_tze6f8epw,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_ossic7t88],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_74w347m3s =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_wu1kc3d7g =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_74w347m3s,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_wu1kc3d7g],
    event: 'close',
  });
  const act_1040167445267876_closeComponent_lni5jznvp =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_lni5jznvp_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_ji0pzfygc =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_lni5jznvp,
      event: 'saved',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_closeComponent_m2kbwm2rm =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_m2kbwm2rm_getActionArgs,
    });
  const inst_evh_1040167445267876_deleted_bjxt8oryv =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_m2kbwm2rm,
      event: 'deleted',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_rest_qfg5vz38s = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_qfg5vz38s_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_1040167445267876_saved_ji0pzfygc,
      inst_evh_1040167445267876_deleted_bjxt8oryv,
    ],
  });
  const inst_evh_1040167445267876_submit_6dnjhfoyu =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_qfg5vz38s,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_6dnjhfoyu],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_fksn9d1b6 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_c8tg9ycdq =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_fksn9d1b6,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_c8tg9ycdq],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_haev4c9f9,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_ossic7t88,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_wu1kc3d7g,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_saved_ji0pzfygc,
    inst_evh_1040167445267876_deleted_bjxt8oryv,
    inst_evh_1040167445267876_submit_6dnjhfoyu,
    submit: inst_evh_1040167445267876_submit_6dnjhfoyu,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_c8tg9ycdq,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_haev4c9f9 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_haev4c9f9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_ossic7t88 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_ossic7t88.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_wu1kc3d7g = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_wu1kc3d7g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_ji0pzfygc = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_ji0pzfygc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_deleted_bjxt8oryv = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_deleted_bjxt8oryv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_6dnjhfoyu = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_6dnjhfoyu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_c8tg9ycdq = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_c8tg9ycdq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Address"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm name="address" @submit="evh_1040167445267876_submit($event, {})">
        <FieldsetAddress :address="editor"> </FieldsetAddress>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded && forms.address">
        <ItpButton
          type="submit"
          :text="gettext('Save')"
          :form="forms.address"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
